import { useState } from "react";
import { Check, MessageCircle, Scissors } from "lucide-react";
import { ChapterLabel, MaskReveal, Reveal } from "@/components/Reveal";
import CommercialModal from "@/components/CommercialModal";

const FEATURES = [
  "Bulk garment care with dependable turnaround",
  "Hotel linen, bedding & towel services",
  "Staff uniform cleaning & pressing",
  "Flexible scheduled pickups for your business",
];

export default function Commercial() {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <section
      id="commercial"
      data-testid="commercial-section"
      className="relative border-y border-[#D4AF37]/10 bg-[#0E1420] py-28 sm:py-36"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-2 lg:gap-12">
        <div>
          <ChapterLabel index="05" title="Commercial & Hotels" testId="chapter-commercial" tone="dark" />
          <h2 className="mt-8 font-display text-4xl font-light leading-[1.08] tracking-tight text-[#F9F6F0] sm:text-5xl">
            <MaskReveal>A quiet partner behind</MaskReveal>
            <MaskReveal delay={0.12}>
              <em className="gold-text-bright font-medium italic">flawless hospitality.</em>
            </MaskReveal>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-lg text-base font-light leading-relaxed tracking-wide text-[#A8B0BC]">
              We provide tailored laundry and dry cleaning partnerships for local
              hotels, hospitality businesses and corporate clients. From crisp
              bedding to immaculate staff uniforms, your guests and your team
              feel the difference.
            </p>
          </Reveal>
          <ul className="mt-10 space-y-4">
            {FEATURES.map((feature, i) => (
              <Reveal key={feature} delay={0.25 + i * 0.08}>
                <li className="flex items-start gap-4 text-sm font-light tracking-wide text-[#F9F6F0]/85">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-[#D4AF37]/40">
                    <Check className="h-3 w-3 text-[#D4AF37]" strokeWidth={2} />
                  </span>
                  {feature}
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.55}>
            <button
              data-testid="commercial-whatsapp-btn"
              onClick={() => setModalOpen(true)}
              className="group relative mt-12 inline-flex items-center gap-3 overflow-hidden bg-[#D4AF37] px-10 py-5 text-xs font-semibold uppercase tracking-[0.28em] text-[#0E1420] transition-colors duration-300 hover:bg-[#F3E5AB]"
            >
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
              <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
              Enquire via WhatsApp
            </button>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div
            data-testid="commercial-panel"
            className="relative overflow-hidden border border-[#D4AF37]/20 bg-[#151C2A] p-10 shadow-[0_4px_24px_rgba(26,26,26,0.04)] sm:p-14"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56">
              <div className="absolute inset-0 animate-spin-slow rounded-full border border-dashed border-[#D4AF37]/25" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Scissors className="h-6 w-6 text-[#D4AF37]/60" strokeWidth={1} />
              </div>
            </div>
            <p className="text-[10px] font-medium uppercase tracking-[0.45em] text-[#D4AF37]">
              R&J For Business
            </p>
            <p className="mt-8 font-display text-6xl font-light italic leading-none text-[#F9F6F0] sm:text-7xl">
              B2B<span className="gold-text-bright">.</span>
            </p>
            <p className="mt-8 max-w-xs text-sm font-light leading-relaxed tracking-wide text-[#A8B0BC]">
              One dedicated point of contact, consistent quality, and schedules
              built around your operation — not ours.
            </p>
            <div className="mt-12 border-t border-[#D4AF37]/15 pt-8">
              <p className="text-[11px] uppercase tracking-[0.35em] leading-loose text-[#F9F6F0]/70">
                Hotels · Restaurants · Spas
                <br />
                Corporate Offices · Guest Houses
              </p>
            </div>
          </div>
        </Reveal>
      </div>
      <CommercialModal open={modalOpen} onOpenChange={setModalOpen} />
    </section>
  );
}
